import {
  createContext,
  createElement,
  useContext,
  useEffect,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import i18n from "../i18n/index.js";
import { apiGet } from "./api.js";
import { useMe } from "./auth.js";

export interface PublicAppConfig {
  setupCompleted: boolean;
  defaultLocale: "en" | "de" | "fr" | "es";
  companyCountry: string;
  branding: {
    enabled: boolean;
    name: string;
    logoUrl: string | null;
  };
}

export const APP_CONFIG_QUERY_KEY = ["app-config"] as const;

export const DEFAULT_APP_CONFIG: PublicAppConfig = {
  setupCompleted: false,
  defaultLocale: "en",
  companyCountry: "CH",
  branding: {
    enabled: false,
    name: "Offertli",
    logoUrl: null,
  },
};

interface AppConfigContextValue {
  config: PublicAppConfig;
  isLoading: boolean;
}

const AppConfigContext = createContext<AppConfigContextValue>({
  config: DEFAULT_APP_CONFIG,
  isLoading: true,
});

export function AppConfigProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: APP_CONFIG_QUERY_KEY,
    queryFn: () => apiGet<PublicAppConfig>("/app-config"),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const session = useMe();
  const config = query.data ?? DEFAULT_APP_CONFIG;

  useEffect(() => {
    document.title = config.branding.name;
  }, [config.branding.name]);

  useEffect(() => {
    if (
      query.data?.setupCompleted &&
      session.isError &&
      i18n.language !== query.data.defaultLocale
    ) {
      void i18n.changeLanguage(query.data.defaultLocale);
    }
  }, [query.data?.defaultLocale, query.data?.setupCompleted, session.isError]);

  return createElement(
    AppConfigContext.Provider,
    { value: { config, isLoading: query.isLoading } },
    children,
  );
}

export function useAppConfig(): PublicAppConfig {
  return useContext(AppConfigContext).config;
}

export function useAppConfigLoading(): boolean {
  return useContext(AppConfigContext).isLoading;
}
